import { helpers } from "vuelidate/lib/validators"
import dayjs from "dayjs"
import { PayFrequency } from "@/model/apply/PayFrequencies"

const ALPHA_LOWER_RANGE = "a-z"
const ALPHA_LOWER_CHARS = [
    "a",
    "b",
    "c",
    "d",
    "e",
    "f",
    "g",
    "h",
    "i",
    "j",
    "k",
    "l",
    "m",
    "n",
    "o",
    "p",
    "q",
    "r",
    "s",
    "t",
    "u",
    "v",
    "w",
    "x",
    "y",
    "z",
]

const ALPHA_UPPER_RANGE = "A-Z"
const ALPHA_UPPER_CHARS = [
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "W",
    "X",
    "Y",
    "Z",
]

const DIGIT_RANGE = "0-9"
const DIGIT_CHARS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"]

export const noPoBox = value => {
    if (!value) return true
    return !/^\s*((P(OST)?.?\s*(O(FF(ICE)?)?)?.?\s*(B(IN|OX))?)|B(IN|OX)).*/gi.test(value)
}

noPoBox.id = "noPoBox"

export const maxAge = max =>
    helpers.withParams({ max }, value => {
        if (!helpers.req(value)) {
            return true
        }

        const maxStart = dayjs().startOf("day").subtract(max, "years")
        const valueStart = dayjs(value).startOf("day")

        return valueStart.isSameOrAfter(maxStart)
    })

maxAge.id = "maxAge"

export const minAge = min =>
    helpers.withParams({ min }, value => {
        if (!helpers.req(value)) {
            return true
        }

        const minStart = dayjs().startOf("day").subtract(min, "years")
        const valueStart = dayjs(value).startOf("day")
        return valueStart.isSameOrBefore(minStart)
    })

minAge.id = "minAge"

export const minDate = min =>
    helpers.withParams({ min }, value => {
        if (!helpers.req(value)) {
            return true
        }

        let date = dayjs(value)
        return date.isSameOrAfter(min)
    })
minDate.id = "minDate"

export const maxDate = max =>
    helpers.withParams({ max }, value => {
        if (!helpers.req(value)) {
            return true
        }

        let date = dayjs(value)
        return date.isSameOrBefore(max)
    })
maxDate.id = "maxDate"

export const notInPast =
    (startOf = "day") =>
    value => {
        if (!helpers.req(value)) {
            return true
        }

        let valueStart = dayjs(value)
        if (startOf) valueStart = valueStart.startOf(startOf)

        let nowStart = dayjs()
        if (startOf) nowStart = nowStart.startOf(startOf)

        return valueStart.isSameOrAfter(nowStart)
    }
notInPast.id = "notInPast"

export const notInFuture =
    (startOf = "day") =>
    value => {
        if (!helpers.req(value)) {
            return true
        }

        let valueStart = dayjs(value)
        if (startOf) valueStart = valueStart.startOf(startOf)

        let nowStart = dayjs()
        if (startOf) nowStart = nowStart.startOf(startOf)

        return valueStart.isSameOrBefore(nowStart)
    }
notInFuture.id = "notInFuture"

// noinspection RegExpRedundantEscape
export const validEmail = helpers.regex(
    "email",
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i
)
validEmail.id = "email"

export const validEntityKey = helpers.regex("entityKey", /^[0-9]{16}$/)
validEntityKey.id = "entityKey"

export const validPhoneNumber = helpers.regex("phone", /^[0-9]{10}$/)
validPhoneNumber.id = "phone"

export const validSsn = helpers.regex("ssn", /^[0-9]{9}$/)
validSsn.id = "ssn"

export const validZipCode = helpers.regex("zip", /^[0-9]{5}(-[0-9]{4})?$/)
validZipCode.id = "zipCode"

export const validChars = rawChars => {
    const chars = new Set(
        (rawChars || []).flatMap(ch => {
            let expanded

            switch (ch) {
                case ALPHA_LOWER_RANGE: {
                    expanded = ALPHA_LOWER_CHARS
                    break
                }
                case ALPHA_UPPER_RANGE:
                    expanded = ALPHA_UPPER_CHARS
                    break
                case DIGIT_RANGE:
                    expanded = DIGIT_CHARS
                    break
                default: {
                    expanded = [ch]
                }
            }

            return expanded
        })
    )

    return helpers.withParams(
        add =>
            function (value) {
                if (!helpers.req(value)) return true
                if (!value) return true

                const invalidChars = []

                for (let idx in value) {
                    const ch = value.charAt(idx)
                    if (invalidChars.includes(ch)) continue
                    if (!chars.has(ch)) invalidChars.push(ch)
                }

                add({ valid: rawChars.join(""), invalid: invalidChars.join("") })

                return !invalidChars.length
            }
    )
}

validChars.id = "validChars"

export const validPassword = function (value) {
    return (
        !helpers.req(value) ||
        (value.length >= 8 &&
            /[a-z]/.test(value) &&
            /[A-Z]/.test(value) &&
            /[0-9]/.test(value) &&
            /[!@#$%^&*]/.test(value))
    )
}
validPassword.id = "password"

export const requireOne = fieldIds =>
    helpers.withParams({ fieldIds }, (value, _, vm) => {
        if (helpers.req(value)) {
            return true
        }

        let fieldsPresent = fieldIds.filter(fieldId => vm.hasOwnProperty(fieldId))

        if (fieldsPresent.length === 1) {
            return false
        }

        for (let fieldId of fieldsPresent) {
            if (helpers.req(vm[fieldId])) {
                return true
            }
        }

        return false
    })
requireOne.id = "requireOne"

export const buildError = (id, value, label, params) => {
    return {
        id: id,
        value: value,
        params: { field: label, ...(params || {}) },
    }
}

export const payDateNotValidForFrequency = function (value, parentVm) {
    const payFrequency = helpers.ref("primaryIncomePayFrequency", this, parentVm)
    const lastPayDateStr = helpers.ref("primaryIncomePreviousPayDate", this, parentVm)
    const nextPayDateStr = helpers.ref("primaryIncomeNextPayDate", this, parentVm)
    const lastPayDate = dayjs(lastPayDateStr).toDate()
    const nextPayDate = dayjs(nextPayDateStr).toDate()

    let diff = dayjs(nextPayDate).diff(lastPayDate, "d")

    if (!Number.isNaN(diff)) {
        if (payFrequency === PayFrequency.WEEKLY) {
            return diff === 7
        } else if (payFrequency === PayFrequency.BI_WEEKLY) {
            return diff === 14
        } else if (payFrequency === PayFrequency.SEMI_MONTHLY) {
            return diff >= 13 && diff < 18
        } else if (payFrequency === PayFrequency.MONTHLY) {
            return diff >= 27 && diff < 32
        }
    }

    return false
}

export const streetValidation = function (value, parentVm) {
    const line1 = helpers.ref("primaryHomeAddressLine1", this, parentVm)

    return !(line1 === value && line1 != null)
}

export const confirmEmailValidation = function (value, parentVm, applicantType) {
    const confirmEmail = helpers.ref(`${applicantType}ConfirmEmail`, this, parentVm)
    const noActiveEmail = helpers.ref(`${applicantType}NoActiveEmail`, this, parentVm)
    if (!noActiveEmail) {
        return value === confirmEmail
    }
    return true
}

export default {
    maxAge,
    minAge,
    minDate,
    maxDate,
    notInFuture,
    notInPast,
    noPoBox,
    streetValidation,
    validEmail,
    validPhoneNumber,
    validSsn,
    validZipCode,
    requireOne,
    payDateNotValidForFrequency,
    confirmEmailValidation,
}
